import { z } from "zod";
import { briefMissing, ContentKind, drawnStyle, Format, type Brief, type BriefDraft } from "./brief.js";
import { VOICES } from "./plan.js";
import { ANIMATIONS } from "./animations.js";
import { SAID_TOKENS, unsaid } from "./said.js";
import { VIDEO_STYLES } from "./scene.js";

/**
 * One turn of the Studio conversation (03 §the conversation, ADR 0031).
 *
 * The owner says what they want to make in their own words, and a model — the Studio guide — works out what that
 * decides, suggests the rest from the idea and the business page, and asks about what is still missing. The fixed
 * questions could do none of that: typing "a quick video for Instagram stories" where a button was expected was an
 * error.
 *
 * The Business page must never hold an invention; the Studio guide's job is to propose. So the answer carries two things apart:
 * what the owner's words decided (`said`), and what the guide suggests (`proposed`). What either may change is decided
 * here, in code, and not in the prompt:
 *
 * - what the owner said wins over anything, and a suggestion never replaces what the owner decided;
 * - the idea and the photos are never suggested: the idea is the owner's, and photos are chosen where they can be seen;
 * - photos are picked by numbers from the list the guide was given, and a number that was not offered is dropped;
 * - a voice is one of the catalogue's, or none;
 * - an animation is one of the catalogue's, or none, and never suggested: the owner picks it from the chips, or names it;
 * - words on screen are refused when they hold a number, a web address or a handle that is neither in what the owner
 *   said nor on the business page, because a wrong price or phone number on a video is worse than none;
 * - Generate is a button. Nothing here can spend anything.
 */

/** How much of the conversation the guide is given. */
export const STUDIO_HISTORY = 30;
/** The longest message the owner can send at once. */
export const STUDIO_SAID_MAX = 2_000;
/** How many of the owner's photos the guide is shown, the ones already chosen first. */
export const PHOTOS_OFFERED = 40;

/** The parts of the brief, by the names the guide is told. `photos` is `sourceMediaIds`; `seconds` is `durationMs`. */
export const TURN_FIELDS = ["idea", "kind", "format", "photos", "voice", "onScreenText", "seconds", "animation"] as const;
export type TurnField = (typeof TURN_FIELDS)[number];

const BRIEF_KEY: Record<TurnField, keyof Brief> = {
  idea: "idea", kind: "kind", format: "format", photos: "sourceMediaIds", voice: "voice", onScreenText: "onScreenText", seconds: "durationMs", animation: "animation",
};
const TURN_KEY = Object.fromEntries(Object.entries(BRIEF_KEY).map(([t, b]) => [b, t])) as Record<keyof Brief, TurnField>;

/** The parts a guide may suggest. Never the idea, and never the photos. */
export const PROPOSABLE = ["kind", "format", "voice", "onScreenText", "seconds"] as const;

const Said = z.object({
  idea: z.string().nullable(),
  kind: ContentKind.nullable(),
  format: Format.nullable(),
  /** Numbers from the photo list. An empty list means none: pictures are generated. */
  photos: z.array(z.number().int()).nullable(),
  /** A voice's id, or "off" for no narration: nothing is spoken, and a video with no voice has no captions either. */
  voice: z.string().nullable(),
  onScreenText: z.string().nullable(),
  seconds: z.number().nullable(),
  /** An animation's id or name, or "none". Never suggested. */
  animation: z.string().nullable(),
});

const Proposed = Said.pick({ kind: true, format: true, voice: true, onScreenText: true, seconds: true });

export const StudioTurnInput = z.object({
  /** The business's page, as the Business tab shows it (`profileMarkdown`). */
  business: z.string(),
  /** The brief as it stands, every part present and null where nothing is decided. */
  brief: Said,
  /** Which parts of the brief are the guide's own suggestions, and not the owner's. */
  proposed: z.array(z.enum(TURN_FIELDS)),
  /** What Generate still waits for, in the order the Studio asks it. */
  missing: z.array(z.enum(TURN_FIELDS)),
  photos: z.array(z.object({ number: z.number().int().min(1), caption: z.string(), from: z.string() })).max(PHOTOS_OFFERED),
  /** The style's name when the video is drawn (ADR 0050, 0053): it uses no photos and makes no pictures. */
  drawn: z.string().max(40).optional(),
  history: z.array(z.object({ role: z.enum(["owner", "guide"]), text: z.string() })).max(STUDIO_HISTORY),
  said: z.string().max(STUDIO_SAID_MAX),
});
export type StudioTurnInput = z.infer<typeof StudioTurnInput>;

export const StudioTurnOutput = z.object({
  /** What the guide says back, in the owner's language. */
  reply: z.string(),
  /** What the owner's words decided, and null for everything they did not. */
  said: Said,
  /** What the guide suggests for what is not decided, and null for the rest. */
  proposed: Proposed,
});
export type StudioTurnOutput = z.infer<typeof StudioTurnOutput>;

/** Where a picture came from, as the guide is told it. */
const FROM: Record<string, string> = { uploaded: "uploaded by the owner", website: "from their website", google: "from their Google listing", web: "found online", generated: "made for an earlier post" };

/** One of the owner's pictures, as the turn reads it. */
export interface LibraryPhoto {
  id: string;
  caption: string | null;
  origin: string;
}

/** A project's messages, as a turn reads them. */
export interface StudioMessage {
  role: string;
  content: { text?: string };
}

/** Everything one turn is given, and the photo ids behind the numbers in it. */
export function studioTurnInput(p: {
  business: string;
  draft: BriefDraft;
  proposed: readonly string[];
  missing: readonly string[];
  library: readonly LibraryPhoto[];
  messages: readonly StudioMessage[];
  said: string;
}): { input: StudioTurnInput; offered: string[] } {
  const chosen = new Set(p.draft.sourceMediaIds ?? []);
  const ordered = [...p.library.filter((m) => chosen.has(m.id)), ...p.library.filter((m) => !chosen.has(m.id))].slice(0, PHOTOS_OFFERED);
  const offered = ordered.map((m) => m.id);
  const d = p.draft;
  // A drawn video has no photos to ask about: the chat asked for them anyway, and offered to generate pictures, for a
  // video picked from the gallery as drawn (dev box, 2026-09-25).
  const drawn = d.kind === "video" && d.look === "drawn" ? VIDEO_STYLES.find((s) => s.id === (drawnStyle(d) ?? "clean"))!.name : undefined;
  const turnKeys = (keys: readonly string[]) => keys.flatMap((k) => (k in TURN_KEY && !(drawn && k === "sourceMediaIds") ? [TURN_KEY[k as keyof Brief]] : []));
  return {
    offered,
    input: {
      business: p.business,
      brief: {
        idea: d.idea ?? null,
        kind: d.kind ?? null,
        format: d.format ?? null,
        photos: d.sourceMediaIds ? d.sourceMediaIds.flatMap((id) => (offered.includes(id) ? [offered.indexOf(id) + 1] : [])) : null,
        voice: d.voice ? (d.voice.enabled ? d.voice.voiceId ?? VOICES[0]!.id : "off") : null,
        onScreenText: d.onScreenText ?? null,
        seconds: d.durationMs ? Math.round(d.durationMs / 1000) : null,
        animation: d.animation ? (d.animation.enabled ? d.animation.animationId ?? "none" : "none") : null,
      },
      proposed: turnKeys(p.proposed),
      missing: turnKeys(p.missing),
      photos: drawn ? [] : ordered.map((m, i) => ({ number: i + 1, caption: m.caption ?? "(not described yet)", from: FROM[m.origin] ?? m.origin })),
      ...(drawn ? { drawn } : {}),
      history: p.messages
        .filter((m) => (m.role === "user" || m.role === "assistant") && m.content.text?.trim())
        .slice(-STUDIO_HISTORY)
        .map((m) => ({ role: m.role === "user" ? ("owner" as const) : ("guide" as const), text: m.content.text!.trim() })),
      said: p.said,
    },
  };
}

/** What a turn did to the brief. */
export interface AppliedStudioTurn {
  draft: BriefDraft;
  /** The parts of the brief that are suggestions, by their brief names. */
  proposed: Array<keyof Brief>;
  /** What the owner's words set this turn, kept on their message. Null is a part they took away. */
  said: Record<string, unknown>;
  /** What the guide suggested this turn, kept on its message. */
  suggested: Record<string, unknown>;
  /** The parts code refused, by the names the guide used. Logged, not shown. */
  refused: TurnField[];
}

/**
 * A turn's answer applied to the brief, under the rules above. Pure: the caller reads the project, and writes back what
 * this returns.
 */
export function applyStudioTurn(
  stored: { draft: BriefDraft; proposed: readonly string[] },
  out: StudioTurnOutput,
  said: string,
  context: { offered: readonly string[]; business: string },
): AppliedStudioTurn {
  const draft: BriefDraft = { ...stored.draft };
  const proposed = new Set(stored.proposed.filter((k): k is keyof Brief => k in TURN_KEY));
  const owners: Record<string, unknown> = {};
  const suggested: Record<string, unknown> = {};
  const refused: TurnField[] = [];

  // What each part becomes, or `undefined` for a value that is not allowed. `null` takes an optional part away.
  const valueOf = (field: TurnField, raw: unknown, known: string): BriefDraft[keyof Brief] | null | undefined => {
    switch (field) {
      case "idea": {
        const idea = String(raw).trim().slice(0, 500);
        return idea.length >= 3 ? idea : undefined;
      }
      case "kind":
      case "format":
        return raw as BriefDraft["kind"];
      case "photos": {
        const ids = [...new Set((raw as number[]).flatMap((n) => (Number.isInteger(n) && context.offered[n - 1] ? [context.offered[n - 1]!] : [])))];
        // Numbers were asked for and none of them was on the list: that is not "no photos".
        return (raw as number[]).length && !ids.length ? undefined : ids.slice(0, 30);
      }
      case "voice": {
        const v = String(raw).trim().toLowerCase();
        if (v === "off" || v === "none") return { enabled: false };
        const voice = VOICES.find((x) => x.id === v || x.name.toLowerCase() === v);
        return voice ? { enabled: true, voiceId: voice.id } : undefined;
      }
      case "onScreenText": {
        const text = String(raw).trim().slice(0, 120);
        if (!text) return null;
        return unsaid(text, SAID_TOKENS.number, known) || unsaid(text, SAID_TOKENS.address, known) ? undefined : text;
      }
      case "animation": {
        const a = String(raw).trim().toLowerCase();
        if (a === "none" || a === "off" || a === "no") return { enabled: false };
        const found = ANIMATIONS.find((x) => x.id === a || x.name.toLowerCase() === a);
        return found ? { enabled: true, animationId: found.id } : undefined;
      }
      case "seconds": {
        const s = Math.round(Number(raw));
        return Number.isFinite(s) ? Math.min(60, Math.max(5, s)) * 1000 : undefined;
      }
    }
  };

  const set = (field: TurnField, value: BriefDraft[keyof Brief] | null, into: Record<string, unknown>) => {
    const key = BRIEF_KEY[field];
    if (value === null) delete draft[key];
    else (draft as Record<string, unknown>)[key] = value;
    into[key] = value;
  };

  // The owner first: what they said is decided, and theirs.
  for (const field of TURN_FIELDS) {
    const raw = out.said[field];
    if (raw === null) continue;
    const value = valueOf(field, raw, `${said}\n${context.business}\n${stored.draft.onScreenText ?? ""}`);
    if (value === undefined) { refused.push(field); continue; }
    set(field, value, owners);
    proposed.delete(BRIEF_KEY[field]);
  }

  // The Studio's first question is "What is this about?", so while there is no idea the owner's answer is it. The
  // guide once left it out and asked for "the one sentence" again, straight after being given one (dev box,
  // 2026-09-25). A greeting, a yes or a question is not an idea.
  if (stored.draft.idea === undefined && draft.idea === undefined) {
    const text = said.trim();
    if (text.split(/\s+/).length >= 3 && !text.endsWith("?")) set("idea", text.slice(0, 500), owners);
  }

  // Then the guide: only where nothing is decided, or where what is there is already its own suggestion.
  for (const field of PROPOSABLE) {
    const raw = out.proposed[field];
    if (raw === null) continue;
    const key = BRIEF_KEY[field];
    if (key in owners) continue;
    if (draft[key] !== undefined && !proposed.has(key)) continue;
    const value = valueOf(field, raw, `${said}\n${context.business}`);
    if (value === undefined) { refused.push(field); continue; }
    // An empty suggestion is no suggestion.
    if (value === null) continue;
    set(field, value, suggested);
    proposed.add(key);
  }

  return { draft, proposed: [...proposed], said: owners, suggested, refused };
}

/**
 * A suggestion taken back, or an answer changed by a button: whatever the patch sets and whatever is cleared is the
 * owner's decision now, so none of it is a suggestion any more.
 */
export function withAnswer(stored: { draft: BriefDraft; proposed: readonly string[] }, patch: BriefDraft, clear: ReadonlyArray<keyof Brief> = []): { draft: BriefDraft; proposed: string[] } {
  const draft: BriefDraft = { ...stored.draft, ...patch };
  for (const key of clear) delete draft[key];
  const touched = new Set<string>([...Object.keys(patch), ...clear]);
  return { draft, proposed: stored.proposed.filter((k) => !touched.has(k)) };
}

/** What the questions after the idea are answered with when they are skipped. */
export const QUESTION_DEFAULTS: Required<Pick<Brief, "kind" | "format" | "sourceMediaIds" | "voice" | "animation">> = {
  kind: "video", format: "story", sourceMediaIds: [], voice: { enabled: true, voiceId: VOICES[0]!.id }, animation: { enabled: false },
};

/**
 * The questions after the idea, answered by default (`STUDIO_QUESTIONS=skip`, 2026-09-14, at the owner's request while the
 * flow is being tried out).
 *
 * The moment an idea first lands in a draft, every part still missing is filled in and marked a suggestion, so the plan
 * and Generate are one sentence away and each default is a tap to take back. It fills once, when the idea arrives, so a
 * default taken back stays taken back and its question is asked as usual. Generate is still a button.
 */
export function skipQuestions(before: BriefDraft, next: { draft: BriefDraft; proposed: readonly string[] }, said = ""): { draft: BriefDraft; proposed: string[]; filled: Array<keyof Brief> } {
  const draft: BriefDraft = { ...next.draft };
  const filled: Array<keyof Brief> = [];
  if (!before.idea && draft.idea) {
    // The kind first: whether the animation is asked at all depends on it.
    if (draft.kind === undefined) { draft.kind = QUESTION_DEFAULTS.kind; filled.push("kind"); }
    const defaults = QUESTION_DEFAULTS as Record<string, unknown>;
    // Someone who says it is their photo is asked which, never handed "Generated pictures" to take back first
    // (2026-09-27: the picker could not be seen until the suggestion was undone).
    const theirs = saysOwnPhoto(`${said} ${draft.idea}`);
    for (const key of briefMissing(draft)) {
      if (!(key in defaults) || (key === "sourceMediaIds" && theirs)) continue;
      (draft as Record<string, unknown>)[key] = structuredClone(defaults[key]);
      filled.push(key);
    }
  }
  return { draft, proposed: [...new Set([...next.proposed, ...filled])], filled };
}

/** Whether someone's words say the picture is theirs: "my photo", "our pictures", "this image of the shop". */
export const saysOwnPhoto = (text: string) => /\b(my|our|this|these|the attached)\s+(own\s+)?(photo|picture|image|pic|shot|snap)s?\b/i.test(text);

/** What `briefs.derived` records at Generate: every part that was the guide's suggestion when the button was pressed. */
export const derivedOf = (proposed: readonly string[]): Record<string, "proposed"> =>
  Object.fromEntries(proposed.map((k) => [k, "proposed" as const]));
